import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { IconChevronLeft } from '@tabler/icons-react';

import { contactEmailFor, type ListingDetails } from './listingDetails';
import PropertySearchForm from './PropertySearchForm';
import { useListingNavigation } from './useListingNavigation';

/**
 * `/make-offer/<slug>` for a home in renovation — the legacy portal's
 * `homeInRenoNotification` as a full page, per design: offers on these homes
 * go through the market's buyer team by email, or the agent searches for a
 * different home.
 */
const ListingInRenovation = ({ listing }: { listing: ListingDetails }) => {
  const contactEmail = contactEmailFor(listing);
  const goToListing = useListingNavigation();

  return (
    <Box as="section" className="w-full justify-center bg-od-bg-secondary px-6 py-6 lg:px-10">
      <Col className="relative w-full max-w-[1320px] items-center gap-6 rounded-[32px] bg-od-bg-primary px-6 py-[156px] lg:rounded-[48px]">
        {/* Positioned by a Box: the Link's own display classes do not compose
            with absolute positioning reliably. */}
        <Box className="absolute top-8 left-8">
          <Link
            href="/make-offer/start"
            aria-label="Back to search"
            analyticsName="cosmos-make-offer-renovation-back"
            className="flex size-10 items-center justify-center rounded-full bg-od-bg-secondary text-od-content-primary no-underline"
          >
            <IconChevronLeft className="size-5" />
          </Link>
        </Box>
        <Image
          src="https://images.opendoor.com/source/s3/imgdrop-production/2026-08-1786044149227-25844_home_in_renovation.svg?service=imgdrop"
          alt=""
          className="w-[235px]"
        />
        <Col className="max-w-[520px] items-center gap-3 text-center">
          <Text
            as="h1"
            className="font-medium text-[28px] tracking-[-1.4px] text-od-content-primary"
          >
            This home is in renovation
          </Text>
          <Text className="text-[18px] leading-[26px] tracking-[-0.5px] text-od-content-secondary">
            To submit an offer on this home, please email{' '}
            <Link
              href={`mailto:${contactEmail}`}
              className="text-od-content-accent-brand"
              analyticsName="cosmos-make-offer-renovation-email"
            >
              {contactEmail}
            </Link>
          </Text>
        </Col>
        <Row className="h-[2px] w-[112px] bg-od-border-primary" />
        <Col className="w-full max-w-[562px] items-center gap-5">
          <Text className="text-[18px] tracking-[-0.5px] text-od-content-secondary">
            or search a different home
          </Text>
          <PropertySearchForm onSubmit={goToListing} />
        </Col>
      </Col>
    </Box>
  );
};

export default ListingInRenovation;
